import {
  DocumentExtentions,
  PDF_MIME_TYPES,
} from "@vega-core/constants/documents";
import { MAX_FILE_SIZE } from "@vega-core/constants/global";

import type { NodeMap, TreeNode } from "../types";

const DATA_ROOM_MIME_TYPES: Record<string, string[]> = {
  ...PDF_MIME_TYPES,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    DocumentExtentions.DOCX,
  ],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    DocumentExtentions.PPTX,
  ],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    DocumentExtentions.XLSX,
  ],
};

type FileEntry = {
  kind: "file";
  name: string;
  file: File;
};

type FolderEntry = {
  kind: "folder";
  name: string;
  children: DropEntry[];
};

export type DropEntry = FileEntry | FolderEntry;

export type DroppedFile = {
  nodeId: string;
  resolvedName: string;
  file: File;
};

export type MergeTarget = {
  existingFolderId: string;
  newChildIds: string[];
};

export type ConflictResolution = "detect" | "keep-both" | "replace";

export type FileConflict = {
  existingNodeId: string;
  droppedName: string;
  droppedFile: File;
  parentPath: string;
  fullPath: string;
};

export type FileReplacement = {
  existingNodeId: string;
  file: File;
};

export type BuiltTreeResult = {
  nodes: TreeNode[];
  fileEntries: DroppedFile[];
  mergeTargets: MergeTarget[];
  conflicts: FileConflict[];
  replacements: FileReplacement[];
};

type FileValidationResult = {
  valid: DroppedFile[];
  rejected: RejectedFile[];
};

export type RejectedFile = {
  name: string;
  reason: "size" | "type";
};

export const FILE_INPUT_ACCEPT = Object.entries(DATA_ROOM_MIME_TYPES)
  .flatMap(([mime, exts]) => [mime, ...exts.map((e) => `.${e}`)])
  .join(",");

const ACCEPTED_MIME_TYPES = new Set(Object.keys(DATA_ROOM_MIME_TYPES));
const ACCEPTED_EXTENSIONS = new Set(
  Object.values(DATA_ROOM_MIME_TYPES)
    .flat()
    .map((ext) => `.${ext.toLowerCase()}`),
);

function getEntry(item: DataTransferItem): FileSystemEntry | null {
  // Future-proof: spec may standardize as getAsEntry()
  if ("getAsEntry" in item && typeof item.getAsEntry === "function") {
    return (
      item as DataTransferItem & { getAsEntry: () => FileSystemEntry | null }
    ).getAsEntry();
  }

  return item.webkitGetAsEntry() ?? null;
}

function readFileSystemFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

function readAllDirectoryEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const allEntries: FileSystemEntry[] = [];

    function readBatch() {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(allEntries);
        } else {
          allEntries.push(...batch);
          // readEntries may return at most ~100 entries per call
          readBatch();
        }
      }, reject);
    }

    readBatch();
  });
}

async function processDirectoryEntry(
  entry: FileSystemDirectoryEntry,
): Promise<FolderEntry> {
  const reader = entry.createReader();
  const childEntries = await readAllDirectoryEntries(reader);
  const children: DropEntry[] = [];

  for (const child of childEntries) {
    if (child.isFile) {
      const file = await readFileSystemFile(child as FileSystemFileEntry);

      if (isAcceptedType(file)) {
        children.push({ kind: "file", name: child.name, file });
      }
    } else if (child.isDirectory) {
      children.push(
        await processDirectoryEntry(child as FileSystemDirectoryEntry),
      );
    }
  }

  return { kind: "folder", name: entry.name, children };
}

async function processFileEntry(
  entry: FileSystemFileEntry,
): Promise<FileEntry | null> {
  const file = await readFileSystemFile(entry);

  if (!isAcceptedType(file)) return null;

  return { kind: "file", name: entry.name, file };
}

async function readFileSystemDropEntries(
  fsEntries: (FileSystemEntry | null)[],
): Promise<DropEntry[]> {
  const entries: DropEntry[] = [];

  for (const fsEntry of fsEntries) {
    if (!fsEntry) continue;

    if (fsEntry.isDirectory) {
      entries.push(
        await processDirectoryEntry(fsEntry as FileSystemDirectoryEntry),
      );
      continue;
    }

    if (!fsEntry.isFile) continue;

    const fileEntry = await processFileEntry(fsEntry as FileSystemFileEntry);

    if (fileEntry) {
      entries.push(fileEntry);
    }
  }

  return entries;
}

function readFallbackFileEntries(
  items: DataTransferItem[] | DataTransferItemList,
): DropEntry[] {
  const entries: DropEntry[] = [];

  for (let i = 0; i < items.length; i++) {
    if (items[i].kind !== "file") continue;

    const file = items[i].getAsFile();

    if (file && isAcceptedType(file)) {
      entries.push({ kind: "file", name: file.name, file });
    }
  }

  return entries;
}

export async function readDropEntries(
  items: DataTransferItem[] | DataTransferItemList,
): Promise<DropEntry[]> {
  const { length } = items;

  if (length === 0) return [];

  // Collect all FileSystemEntry references synchronously before any async work —
  // browsers may clear DataTransferItemList after the event handler returns
  const fsEntries: (FileSystemEntry | null)[] = [];

  for (let i = 0; i < length; i++) {
    if (items[i].kind === "file") {
      fsEntries.push(getEntry(items[i]));
    }
  }

  if (fsEntries.some((fsEntry) => fsEntry !== null)) {
    return readFileSystemDropEntries(fsEntries);
  }

  // Fallback: webkitGetAsEntry not supported — read files via getAsFile()
  return readFallbackFileEntries(items);
}

type SiblingLookup = {
  names: Set<string>;
  folderByName: Map<string, TreeNode>;
  fileByName: Map<string, TreeNode>;
};

function getExistingChildren(
  parentId: string | null,
  existingNodeMap: NodeMap,
): TreeNode[] {
  if (parentId === null) {
    return Object.values(existingNodeMap).filter(
      (n): n is TreeNode => n !== undefined && n.parentId === null,
    );
  }

  const parent = existingNodeMap[parentId];

  if (!parent) return [];

  return parent.children
    .map((cid) => existingNodeMap[cid])
    .filter((n): n is TreeNode => n !== undefined);
}

function getSiblingLookup(
  parentId: string | null,
  type: "file" | "folder",
  existingNodeMap: NodeMap,
  newNodes: TreeNode[],
): SiblingLookup {
  const names = new Set<string>();
  const folderByName = new Map<string, TreeNode>();
  const fileByName = new Map<string, TreeNode>();
  const children = getExistingChildren(parentId, existingNodeMap);

  for (const child of children) {
    if (child.type === type) {
      names.add(child.name);

      if (type === "folder") {
        folderByName.set(child.name, child);
      } else {
        fileByName.set(child.name, child);
      }
    }
  }

  for (const n of newNodes) {
    if (n.parentId === parentId && n.type === type) {
      names.add(n.name);
    }
  }

  return { names, folderByName, fileByName };
}

function deduplicateName(name: string, existingNames: Set<string>): string {
  if (!existingNames.has(name)) return name;

  const dotIndex = name.lastIndexOf(".");
  const hasExtension = dotIndex > 0;
  const baseName = hasExtension ? name.slice(0, dotIndex) : name;
  const extension = hasExtension ? name.slice(dotIndex) : "";

  let counter = 1;

  while (existingNames.has(`${baseName} (${counter})${extension}`)) {
    counter++;
  }

  return `${baseName} (${counter})${extension}`;
}

export function buildTreeFromDropEntries(
  entries: DropEntry[],
  parentId: string | null,
  existingNodeMap: NodeMap,
  strategy: ConflictResolution = "keep-both",
): BuiltTreeResult {
  const nodes: TreeNode[] = [];
  const fileEntries: DroppedFile[] = [];
  const mergeTargets: MergeTarget[] = [];
  const conflicts: FileConflict[] = [];
  const replacements: FileReplacement[] = [];

  // Cache lookups per (parentId, type) to avoid re-scanning siblings per node
  const lookupCache = new Map<string, SiblingLookup>();

  function getCachedLookup(
    parentNodeId: string | null,
    type: "file" | "folder",
  ): SiblingLookup {
    const key = `${parentNodeId ?? "null"}:${type}`;
    const cached = lookupCache.get(key);

    if (cached) return cached;

    const lookup = getSiblingLookup(parentNodeId, type, existingNodeMap, nodes);
    lookupCache.set(key, lookup);

    return lookup;
  }

  function queueConflict(
    existingNodeId: string,
    droppedName: string,
    droppedFile: File,
    parentPath: string,
  ) {
    conflicts.push({
      existingNodeId,
      droppedName,
      droppedFile,
      parentPath,
      fullPath: parentPath ? `${parentPath}/${droppedName}` : droppedName,
    });
  }

  function queueReplacement(existingNodeId: string, file: File) {
    replacements.push({ existingNodeId, file });
  }

  function createFileNode(
    entry: FileEntry,
    parentNodeId: string | null,
    parentPath: string,
    fileLookup: SiblingLookup,
  ): string {
    const resolvedName = deduplicateName(entry.name, fileLookup.names);
    fileLookup.names.add(resolvedName);

    const id = crypto.randomUUID();
    const fileNode: TreeNode = {
      id,
      name: resolvedName,
      type: "file",
      parentId: parentNodeId,
      children: [],
      createdAt: new Date().toISOString(),
      path: `${parentPath}/${resolvedName}`,
      isNew: true,
    };
    nodes.push(fileNode);
    fileEntries.push({ nodeId: id, resolvedName, file: entry.file });

    return id;
  }

  function walkFile(
    entry: FileEntry,
    parentNodeId: string | null,
    parentPath: string,
  ): string | null {
    const fileLookup = getCachedLookup(parentNodeId, "file");
    const existingFile = fileLookup.fileByName.get(entry.name);

    if (!existingFile) {
      return createFileNode(entry, parentNodeId, parentPath, fileLookup);
    }

    if (strategy === "detect") {
      queueConflict(existingFile.id, entry.name, entry.file, parentPath);

      return null;
    }

    if (strategy === "replace") {
      queueReplacement(existingFile.id, entry.file);

      return existingFile.id;
    }

    return createFileNode(entry, parentNodeId, parentPath, fileLookup);
  }

  function walkFolder(
    entry: FolderEntry,
    parentNodeId: string | null,
    parentPath: string,
  ): string {
    const folderLookup = getCachedLookup(parentNodeId, "folder");
    const existingFolder = folderLookup.folderByName.get(entry.name);

    if (existingFolder) {
      const existingPath = existingFolder.path ?? `${parentPath}/${entry.name}`;
      const newChildIds: string[] = [];

      for (const child of entry.children) {
        const childId = walkDropEntry(child, existingFolder.id, existingPath);

        if (childId !== null) newChildIds.push(childId);
      }

      if (newChildIds.length > 0) {
        mergeTargets.push({
          existingFolderId: existingFolder.id,
          newChildIds,
        });
      }

      return existingFolder.id;
    }

    const folderPath = `${parentPath}/${entry.name}`;
    const id = crypto.randomUUID();
    const folderNode: TreeNode = {
      id,
      name: entry.name,
      type: "folder",
      parentId: parentNodeId,
      children: [],
      createdAt: null,
      path: folderPath,
      isNew: true,
    };
    nodes.push(folderNode);
    folderLookup.names.add(entry.name);

    for (const child of entry.children) {
      const childId = walkDropEntry(child, id, folderPath);

      if (childId !== null) folderNode.children.push(childId);
    }

    return id;
  }

  function walkDropEntry(
    entry: DropEntry,
    parentNodeId: string | null,
    parentPath: string,
  ): string | null {
    if (entry.kind === "folder") {
      return walkFolder(entry, parentNodeId, parentPath);
    }

    return walkFile(entry, parentNodeId, parentPath);
  }

  const initialPath = parentId ? (existingNodeMap[parentId]?.path ?? "") : "";

  for (const entry of entries) {
    walkDropEntry(entry, parentId, initialPath);
  }

  return { nodes, fileEntries, mergeTargets, conflicts, replacements };
}

function isAcceptedType(file: File): boolean {
  if (ACCEPTED_MIME_TYPES.has(file.type)) return true;

  // Extension fallback for files with empty MIME type
  const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;

  return ACCEPTED_EXTENSIONS.has(ext);
}

export function validateFiles(
  fileEntries: DroppedFile[],
): FileValidationResult {
  const valid: DroppedFile[] = [];
  const rejected: RejectedFile[] = [];

  for (const entry of fileEntries) {
    if (entry.file.size > MAX_FILE_SIZE) {
      rejected.push({ name: entry.resolvedName, reason: "size" });
    } else if (!isAcceptedType(entry.file)) {
      rejected.push({ name: entry.resolvedName, reason: "type" });
    } else {
      valid.push(entry);
    }
  }

  return { valid, rejected };
}
